import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    LuEye,
    LuMessageSquare,
    LuGitMerge,
    LuChartBar,
    LuShieldCheck,
    LuRefreshCw,
    LuRocket,
    LuArrowRight,
    LuServer
} from 'react-icons/lu';

const AIEngineering = () => {
    const [hoveredCapability, setHoveredCapability] = useState<number | null>(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div>
            {/* Hero Section */}
            {/* Hero Section */}
            <section className="relative overflow-hidden bg-primary py-20 lg:py-32">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                    <div className="inline-flex items-center space-x-2 text-blue-100 font-bold tracking-wider text-sm mb-6 uppercase">
                        <span>AI Engineering</span>
                    </div>
                    <h1 className="text-5xl lg:text-7xl font-bold text-white tracking-tight mb-6">
                        Building the Backbone of <br />
                        <span className="text-secondary">Intelligent Enterprise</span>
                    </h1>
                    <p className="text-xl text-gray-100 max-w-3xl mx-auto mb-10 leading-relaxed">
                        We don't just experiment with AI; we engineer it. From custom model architectures to scalable MLOps pipelines, we deliver production-grade AI that drives real business value.
                    </p>
                </div>
            </section>

            {/* Our Capabilities Section */}
            <section className="py-24 bg-slate-50">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center space-x-2 text-blue-600 font-bold tracking-wider text-sm mb-4 uppercase">
                            <span>Our Capabilities</span>
                        </div>
                        <h2 className="text-4xl font-bold text-slate-900 mb-6">Engineering Excellence</h2>
                        <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
                            Our engineering philosophy centers on robustness, scalability, and maintainability. We treat AI models as software, ensuring rigorous testing and seamless integration.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        {[
                            {
                                icon: LuEye,
                                title: "Computer Vision",
                                desc: "Unlock visual intelligence with state-of-the-art architectures. We build systems for defect detection, object recognition, and automated surveillance.",
                                features: ["Object Detection & Tracking", "Image Segmentation", "Visual Quality Control"],
                                color: "bg-blue-500",
                                hex: "#3b82f6"
                            },
                            {
                                icon: LuMessageSquare,
                                title: "Natural Language Processing",
                                desc: "Go beyond simple chatbots. We engineer advanced NLP pipelines for document understanding, sentiment analysis, and semantic search.",
                                features: ["Entity Extraction (NER)", "Intelligent Document Processing", "Multi-lingual Models"],
                                color: "bg-purple-500",
                                hex: "#a855f7"
                            },
                            {
                                icon: LuGitMerge,
                                title: "MLOps & Infrastructure",
                                desc: "The model is only 10% of the work. We build the infrastructure to train, deploy, and monitor models at scale with zero downtime.",
                                features: ["Automated Training Pipelines", "Model Registry & Versioning", "Real-time Inference APIs"],
                                color: "bg-emerald-500",
                                hex: "#10b981"
                            },
                            {
                                icon: LuChartBar,
                                title: "Predictive Analytics",
                                desc: "Turn historical data into future insights. We develop custom regression and forecasting models to optimize supply chains and revenue.",
                                features: ["Demand Forecasting", "Churn Prediction", "Risk Modeling"],
                                color: "bg-amber-500",
                                hex: "#f59e0b"
                            }
                        ].map((item, index) => (
                            <div
                                key={index}
                                className="bg-white rounded-[2rem] p-10 shadow-sm border border-slate-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group"
                                onMouseEnter={() => setHoveredCapability(index)}
                                onMouseLeave={() => setHoveredCapability(null)}
                                style={{
                                    backgroundImage: hoveredCapability === index
                                        ? `linear-gradient(to bottom right, ${item.hex}0d, transparent)`
                                        : undefined
                                }}
                            >
                                <div className={`w-16 h-16 ${item.color} rounded-2xl flex items-center justify-center mb-8 text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}
                                    style={{ boxShadow: `0 10px 15px -3px ${item.hex}4d` }}>
                                    {React.createElement(item.icon as any, { className: "w-8 h-8" })}
                                </div>
                                <h3 className="text-2xl font-bold text-slate-900 mb-4 transition-colors duration-300"
                                    style={{ color: hoveredCapability === index ? item.hex : undefined }}>
                                    {item.title}
                                </h3>
                                <p className="text-slate-600 mb-8 leading-relaxed">{item.desc}</p>
                                <ul className="space-y-3">
                                    {item.features.map((feature, i) => (
                                        <li key={i} className="flex items-center text-slate-700 font-medium">
                                            <span className="w-1.5 h-1.5 bg-slate-400 rounded-full mr-3"
                                                style={{ backgroundColor: hoveredCapability === index ? item.hex : undefined }}></span>
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Methodology Section */}
            <section className="py-24 bg-white overflow-hidden">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <div className="inline-flex items-center space-x-2 text-blue-600 font-bold tracking-wider text-sm mb-4 uppercase">
                                <span>Methodology</span>
                            </div>
                            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">The icubechat Engineering Standard</h2>
                            <p className="text-xl text-slate-600 mb-12 leading-relaxed">
                                Our approach integrates Software Engineering rigor with Data Science flexibility. We ensure that every line of code contributes to a stable, performant system.
                            </p>

                            <div className="space-y-10 mb-12">
                                {[
                                    {
                                        icon: LuShieldCheck,
                                        title: "Production-First:",
                                        desc: "We architect for deployment from Day 1."
                                    },
                                    {
                                        icon: LuRefreshCw,
                                        title: "Continuous Improvement:",
                                        desc: "Automated retraining loops keep models fresh."
                                    },
                                    {
                                        icon: LuShieldCheck, // Using Shield for Secure by Design explicitly if different
                                        title: "Secure by Design:",
                                        desc: "Enterprise-grade security and data privacy protocols."
                                    }
                                ].map((item, index) => (
                                    <div key={index} className="flex items-start">
                                        <div className="flex-shrink-0 mt-1 mr-6">
                                            {React.createElement(item.icon as any, { className: "w-6 h-6 text-slate-900" })}
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                                            <h4 className="text-lg font-bold text-slate-900">{item.title}</h4>
                                            <p className="text-slate-500 font-medium">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <Link
                                to="/contact"
                                className="inline-block bg-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-700 transition-colors shadow-lg shadow-blue-500/25"
                            >
                                Discuss Your Project
                            </Link>
                        </div>

                        <div className="relative">
                            <div className="relative rounded-[2.5rem] overflow-hidden aspect-[4/3] shadow-2xl flex items-center justify-center group bg-slate-900">
                                {/* Abstract Visualization */}
                                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2000&auto=format&fit=crop')] bg-cover bg-center transition-transform duration-700 group-hover:scale-105 opacity-80"></div>

                                <div className="relative z-10 glass-card bg-white/10 backdrop-blur-md border border-white/20 p-12 rounded-3xl text-center shadow-2xl skew-y-1 hover:skew-y-0 transition-transform duration-500">
                                    {React.createElement(LuServer as any, { className: "w-12 h-12 text-white mb-4 mx-auto" })}
                                    <div className="text-lg font-medium text-white">Scalable</div>
                                    <div className="text-white font-bold text-xl">Infrastructure</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            {/* CTA Section */}
            <section className="py-14 bg-white border-t border-gray-100 mt-10">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-[#0B1538] mb-4">
                        Engineer Your Competitive Advantage
                    </h2>
                    <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                        Partner with icubechat to build AI solutions that stand the test of time and scale.
                    </p>
                    <div className="flex justify-center">
                        <Link
                            to="/contact"
                            className="inline-flex items-center px-8 py-4 bg-[#0B25E6] text-white rounded-lg hover:bg-blue-800  transition-colors text-lg font-bold shadow-lg gap-2"
                        >
                            Start Engineering
                            {React.createElement(LuArrowRight as any, { className: "w-5 h-5" })}
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AIEngineering;
